<script setup lang="ts">
import { computed } from 'vue';
import { mdiClose, mdiLifebuoy } from '@quasar/extras/mdi-v7';
import { enrollmentStatusMeta } from '../../utilities/enrollmentStatusMeta';

/**
 * The single explanation surface for an enrollment status. Every status chip in
 * the student UI opens this same dialog — what a status means and what the
 * student can do about it lives here and nowhere else.
 */
const props = defineProps<{
  status?: string | undefined;
  className?: string | undefined;
  schoolName?: string | undefined;
}>();

const open = defineModel<boolean>({ default: false });

const meta = computed(() => enrollmentStatusMeta(props.status));

const subtitle = computed(() =>
  [props.className, props.schoolName].filter(Boolean).join(' · '),
);
</script>

<template>
  <q-dialog v-model="open">
    <q-card class="enrollment-status-card">
      <q-card-section class="row items-center no-wrap q-gutter-sm">
        <q-avatar
          :color="meta.color"
          text-color="white"
          :icon="meta.icon"
          size="md"
        />
        <div class="col">
          <div class="text-subtitle1 text-weight-medium enrollment-status-title">
            {{ $t(meta.labelKey) }}
          </div>
          <div
            v-if="subtitle"
            class="text-caption enrollment-status-subtitle"
          >
            {{ subtitle }}
          </div>
        </div>
        <q-btn
          v-close-popup
          flat
          round
          dense
          :icon="mdiClose"
          :aria-label="$t('CLOSE')"
          class="enrollment-status-subtitle"
        />
      </q-card-section>

      <q-separator />

      <q-card-section class="enrollment-status-body">
        <div class="text-body2">{{ $t(meta.messageKey) }}</div>

        <div class="row items-start no-wrap q-gutter-sm q-mt-md enrollment-status-help">
          <q-icon
            :name="mdiLifebuoy"
            size="sm"
            :color="meta.color"
            class="q-mt-xs"
          />
          <div class="col">
            <div class="text-caption text-uppercase text-weight-medium enrollment-status-subtitle">
              {{ $t('ENROLLMENT_STATUS_WHAT_TO_DO') }}
            </div>
            <div class="text-body2 q-mt-xs">{{ $t(meta.actionKey) }}</div>
          </div>
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-close-popup
          flat
          color="primary"
          :label="$t('CLOSE')"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style scoped lang="scss">
/*
 * Painted from the theme tokens rather than Quasar's `dark` prop so the card
 * tracks the Light/Dark/System preference without every caller passing it down.
 */
.enrollment-status-card {
  width: 100%;
  max-width: 420px;
  background-color: var(--surface-card);
  color: var(--text-primary);
}

.enrollment-status-title {
  color: var(--text-primary);
}

.enrollment-status-subtitle {
  color: var(--text-muted);
}

.enrollment-status-body {
  color: var(--text-secondary);
}

.enrollment-status-help {
  padding: 12px;
  border-radius: 8px;
  background-color: var(--surface-elevated);
  border: 1px solid var(--border-subtle);
}
</style>
